/* @ts-self-types="./prosemirror_rs_wasm.d.ts" */

/**
 * A content match represents a state in the content expression DFA.
 */
export class ContentMatch {
    static __wrap(ptr) {
        const obj = Object.create(ContentMatch.prototype);
        obj.__wbg_ptr = ptr;
        ContentMatchFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ContentMatchFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_contentmatch_free(ptr, 0);
    }
    /**
     * Get the default node type for this content match, if any.
     * @returns {NodeType | undefined}
     */
    defaultType() {
        const ret = wasm.contentmatch_defaultType(this.__wbg_ptr);
        return ret === 0 ? undefined : NodeType.__wrap(ret);
    }
    /**
     * Number of outgoing edges from this state.
     * @returns {number}
     */
    get edgeCount() {
        const ret = wasm.contentmatch_edgeCount(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The next ContentMatch state for the nth outgoing edge.
     * @param {number} n
     * @returns {ContentMatch | undefined}
     */
    edgeMatch(n) {
        const ret = wasm.contentmatch_edgeMatch(this.__wbg_ptr, n);
        return ret === 0 ? undefined : ContentMatch.__wrap(ret);
    }
    /**
     * The node type for the nth outgoing edge.
     * @param {number} n
     * @returns {NodeType | undefined}
     */
    edgeType(n) {
        const ret = wasm.contentmatch_edgeType(this.__wbg_ptr, n);
        return ret === 0 ? undefined : NodeType.__wrap(ret);
    }
    /**
     * Fill this content expression with default nodes before the given fragment.
     * @param {Fragment} after
     * @param {boolean} to_end
     * @param {number} start_index
     * @returns {Fragment | undefined}
     */
    fillBefore(after, to_end, start_index) {
        _assertClass(after, Fragment);
        const ret = wasm.contentmatch_fillBefore(this.__wbg_ptr, after.__wbg_ptr, to_end, start_index);
        return ret === 0 ? undefined : Fragment.__wrap(ret);
    }
    /**
     * Find a wrapping of the given node type.
     * @param {NodeType} target
     * @returns {Array<any> | undefined}
     */
    findWrapping(target) {
        _assertClass(target, NodeType);
        const ret = wasm.contentmatch_findWrapping(this.__wbg_ptr, target.__wbg_ptr);
        return ret;
    }
    /**
     * Match a fragment against this content expression.
     * @param {Fragment} frag
     * @returns {ContentMatch | undefined}
     */
    matchFragment(frag) {
        _assertClass(frag, Fragment);
        const ret = wasm.contentmatch_matchFragment(this.__wbg_ptr, frag.__wbg_ptr);
        return ret === 0 ? undefined : ContentMatch.__wrap(ret);
    }
    /**
     * Match a node type against this content expression, returning the next state.
     * @param {NodeType} type_
     * @returns {ContentMatch | undefined}
     */
    matchType(type_) {
        _assertClass(type_, NodeType);
        const ret = wasm.contentmatch_matchType(this.__wbg_ptr, type_.__wbg_ptr);
        return ret === 0 ? undefined : ContentMatch.__wrap(ret);
    }
    /**
     * True if this match state represents a valid end.
     * @returns {boolean}
     */
    get validEnd() {
        const ret = wasm.contentmatch_validEnd(this.__wbg_ptr);
        return ret !== 0;
    }
}
if (Symbol.dispose) ContentMatch.prototype[Symbol.dispose] = ContentMatch.prototype.free;

/**
 * A fragment represents a node's collection of child nodes.
 */
export class Fragment {
    static __wrap(ptr) {
        const obj = Object.create(Fragment.prototype);
        obj.__wbg_ptr = ptr;
        FragmentFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        FragmentFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_fragment_free(ptr, 0);
    }
    /**
     * Add a node to the end of the fragment.
     * @param {Node} node
     * @returns {Fragment}
     */
    addToEnd(node) {
        _assertClass(node, Node);
        const ret = wasm.fragment_addToEnd(this.__wbg_ptr, node.__wbg_ptr);
        return Fragment.__wrap(ret);
    }
    /**
     * Add a node to the start of the fragment.
     * @param {Node} node
     * @returns {Fragment}
     */
    addToStart(node) {
        _assertClass(node, Node);
        const ret = wasm.fragment_addToStart(this.__wbg_ptr, node.__wbg_ptr);
        return Fragment.__wrap(ret);
    }
    /**
     * Append another fragment's children to this one.
     * @param {Fragment} other
     * @returns {Fragment}
     */
    append(other) {
        _assertClass(other, Fragment);
        const ret = wasm.fragment_append(this.__wbg_ptr, other.__wbg_ptr);
        return Fragment.__wrap(ret);
    }
    /**
     * Get the child at the given index, or null.
     * @param {number} index
     * @returns {Node | undefined}
     */
    child(index) {
        const ret = wasm.fragment_child(this.__wbg_ptr, index);
        return ret === 0 ? undefined : Node.__wrap(ret);
    }
    /**
     * Number of child nodes.
     * @returns {number}
     */
    get childCount() {
        const ret = wasm.fragment_childCount(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Array of child nodes.
     * @returns {Node[]}
     */
    get content() {
        const ret = wasm.fragment_content(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Cut out a sub-fragment between the given positions.
     * @param {number} from
     * @param {number | null} [to]
     * @returns {Fragment}
     */
    cut(from, to) {
        const ret = wasm.fragment_cut(this.__wbg_ptr, from, isLikeNone(to) ? Number.MAX_SAFE_INTEGER : (to) >>> 0);
        return Fragment.__wrap(ret);
    }
    /**
     * True if this fragment is equal to another.
     * @param {Fragment} other
     * @returns {boolean}
     */
    eq(other) {
        _assertClass(other, Fragment);
        const ret = wasm.fragment_eq(this.__wbg_ptr, other.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Find the position and dimensions at which this fragment ends differently.
     * @param {Fragment} other
     * @param {number | null} [pos_a]
     * @param {number | null} [pos_b]
     * @returns {any | undefined}
     */
    findDiffEnd(other, pos_a, pos_b) {
        _assertClass(other, Fragment);
        const ret = wasm.fragment_findDiffEnd(this.__wbg_ptr, other.__wbg_ptr, isLikeNone(pos_a) ? Number.MAX_SAFE_INTEGER : (pos_a) >>> 0, isLikeNone(pos_b) ? Number.MAX_SAFE_INTEGER : (pos_b) >>> 0);
        return ret;
    }
    /**
     * Find the first position at which this fragment differs from another.
     * @param {Fragment} other
     * @param {number | null} [pos]
     * @returns {number | undefined}
     */
    findDiffStart(other, pos) {
        _assertClass(other, Fragment);
        const ret = wasm.fragment_findDiffStart(this.__wbg_ptr, other.__wbg_ptr, isLikeNone(pos) ? Number.MAX_SAFE_INTEGER : (pos) >>> 0);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
    /**
     * The first child, or null if empty.
     * @returns {Node | undefined}
     */
    get firstChild() {
        const ret = wasm.fragment_firstChild(this.__wbg_ptr);
        return ret === 0 ? undefined : Node.__wrap(ret);
    }
    /**
     * Call a function for each direct child node.
     *
     * The callback receives `(node, offset, index)`.
     * @param {Function} f
     */
    forEach(f) {
        const ret = wasm.fragment_forEach(this.__wbg_ptr, f);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Create a fragment from a polymorphic input (Node, Array of Node,
     * Fragment, or null).
     * @param {Schema} schema
     * @param {any} input
     * @returns {Fragment}
     */
    static from(schema, input) {
        _assertClass(schema, Schema);
        const ret = wasm.fragment_from(schema.__wbg_ptr, input);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return Fragment.__wrap(ret[0]);
    }
    /**
     * Create a fragment from an array of child nodes.
     * @param {Schema} schema
     * @param {Node[]} nodes
     * @returns {Fragment}
     */
    static fromArray(schema, nodes) {
        _assertClass(schema, Schema);
        const ptr0 = passArrayJsValueToWasm0(nodes, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.fragment_fromArray(schema.__wbg_ptr, ptr0, len0);
        return Fragment.__wrap(ret);
    }
    /**
     * The last child, or null if empty.
     * @returns {Node | undefined}
     */
    get lastChild() {
        const ret = wasm.fragment_lastChild(this.__wbg_ptr);
        return ret === 0 ? undefined : Node.__wrap(ret);
    }
    /**
     * Like `child`, but returns null if index is out of range.
     * @param {number} index
     * @returns {Node | undefined}
     */
    maybeChild(index) {
        const ret = wasm.fragment_maybeChild(this.__wbg_ptr, index);
        return ret === 0 ? undefined : Node.__wrap(ret);
    }
    /**
     * Call a function for all descendant nodes between two positions.
     *
     * The callback receives `(node, pos, parent, index)`. Return false to stop.
     * @param {number} from
     * @param {number} to
     * @param {Function} f
     */
    nodesBetween(from, to, f) {
        const ret = wasm.fragment_nodesBetween(this.__wbg_ptr, from, to, f);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Replace a child at the given index.
     * @param {number} index
     * @param {Node} node
     * @returns {Fragment}
     */
    replaceChild(index, node) {
        _assertClass(node, Node);
        const ret = wasm.fragment_replaceChild(this.__wbg_ptr, index, node.__wbg_ptr);
        return Fragment.__wrap(ret);
    }
    /**
     * Total size of the fragment (sum of child node sizes).
     * @returns {number}
     */
    get size() {
        const ret = wasm.fragment_size(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Get the text content between two positions.
     * @param {number} from
     * @param {number} to
     * @param {any} block_sep
     * @param {any} leaf_text
     * @returns {string}
     */
    textBetween(from, to, block_sep, leaf_text) {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.fragment_textBetween(this.__wbg_ptr, from, to, block_sep, leaf_text);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * JSON representation of this fragment.
     * @returns {any}
     */
    toJSON() {
        const ret = wasm.fragment_toJSON(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
}
if (Symbol.dispose) Fragment.prototype[Symbol.dispose] = Fragment.prototype.free;

export class MapResult_ {
    static __wrap(ptr) {
        const obj = Object.create(MapResult_.prototype);
        obj.__wbg_ptr = ptr;
        MapResult_Finalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        MapResult_Finalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_mapresult__free(ptr, 0);
    }
    /**
     * Whether the position was deleted (from the side indicated by bias).
     * @returns {boolean}
     */
    get deleted() {
        const ret = wasm.mapresult__deleted(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Whether the position was deleted across.
     * @returns {boolean}
     */
    get deletedAcross() {
        const ret = wasm.mapresult__deletedAcross(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Whether the position was deleted after.
     * @returns {boolean}
     */
    get deletedAfter() {
        const ret = wasm.mapresult__deletedAfter(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Whether the position was deleted before.
     * @returns {boolean}
     */
    get deletedBefore() {
        const ret = wasm.mapresult__deletedBefore(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * The mapped position.
     * @returns {number}
     */
    get pos() {
        const ret = wasm.mapresult__pos(this.__wbg_ptr);
        return ret >>> 0;
    }
}
if (Symbol.dispose) MapResult_.prototype[Symbol.dispose] = MapResult_.prototype.free;

export class Mapping_ {
    static __wrap(ptr) {
        const obj = Object.create(Mapping_.prototype);
        obj.__wbg_ptr = ptr;
        Mapping_Finalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        Mapping_Finalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_mapping__free(ptr, 0);
    }
    /**
     * Append a step map, optionally recording a mirror pair.
     * @param {StepMap_} map
     * @param {number | null} [mirrors]
     */
    appendMap(map, mirrors) {
        _assertClass(map, StepMap_);
        wasm.mapping__appendMap(this.__wbg_ptr, map.__wbg_ptr, isLikeNone(mirrors) ? Number.MAX_SAFE_INTEGER : (mirrors) >>> 0);
    }
    /**
     * Append another mapping's maps to this one.
     * @param {Mapping_} mapping
     */
    appendMapping(mapping) {
        _assertClass(mapping, Mapping_);
        wasm.mapping__appendMapping(this.__wbg_ptr, mapping.__wbg_ptr);
    }
    /**
     * Append the inverse of another mapping's maps in reverse order.
     * @param {Mapping_} mapping
     */
    appendMappingInverted(mapping) {
        _assertClass(mapping, Mapping_);
        wasm.mapping__appendMappingInverted(this.__wbg_ptr, mapping.__wbg_ptr);
    }
    /**
     * Return a deep copy of this mapping.
     * @returns {Mapping_}
     */
    copy() {
        const ret = wasm.mapping__copy(this.__wbg_ptr);
        return Mapping_.__wrap(ret);
    }
    /**
     * Find the mirror of step `n`.
     * @param {number} n
     * @returns {number | undefined}
     */
    getMirror(n) {
        const ret = wasm.mapping__getMirror(this.__wbg_ptr, n);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
    /**
     * Return the inverse of this mapping.
     * @returns {Mapping_}
     */
    invert() {
        const ret = wasm.mapping__invert(this.__wbg_ptr);
        return Mapping_.__wrap(ret);
    }
    /**
     * Map a position through this mapping.
     * @param {number} pos
     * @param {number | null} [bias]
     * @returns {number}
     */
    map(pos, bias) {
        const ret = wasm.mapping__map(this.__wbg_ptr, pos, isLikeNone(bias) ? Number.MAX_SAFE_INTEGER : (bias) >> 0);
        return ret >>> 0;
    }
    /**
     * Map a position, returning a full MapResult.
     * @param {number} pos
     * @param {number | null} [bias]
     * @returns {MapResult_}
     */
    mapResult(pos, bias) {
        const ret = wasm.mapping__mapResult(this.__wbg_ptr, pos, isLikeNone(bias) ? Number.MAX_SAFE_INTEGER : (bias) >> 0);
        return MapResult_.__wrap(ret);
    }
    /**
     * The individual step maps that make up this mapping.
     * @returns {StepMap_[]}
     */
    get maps() {
        const ret = wasm.mapping__maps(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Create a new mapping, optionally initialised with a set of step maps.
     * @param {StepMap_[] | null} [maps]
     */
    constructor(maps) {
        var ptr0 = isLikeNone(maps) ? 0 : passArrayJsValueToWasm0(maps, wasm.__wbindgen_malloc);
        var len0 = WASM_VECTOR_LEN;
        const ret = wasm.mapping__new(ptr0, len0);
        this.__wbg_ptr = ret;
        Mapping_Finalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Record a mirror pair.
     * @param {number} n
     * @param {number} m
     */
    setMirror(n, m) {
        wasm.mapping__setMirror(this.__wbg_ptr, n, m);
    }
    /**
     * Return a sub-mapping for the given range of maps.
     * @param {number} from
     * @param {number | null} [to]
     * @returns {Mapping_}
     */
    slice(from, to) {
        const ret = wasm.mapping__slice(this.__wbg_ptr, from, isLikeNone(to) ? Number.MAX_SAFE_INTEGER : (to) >>> 0);
        return Mapping_.__wrap(ret);
    }
}
if (Symbol.dispose) Mapping_.prototype[Symbol.dispose] = Mapping_.prototype.free;

/**
 * A mark — a piece of metadata attached to a node.
 */
export class Mark {
    static __wrap(ptr) {
        const obj = Object.create(Mark.prototype);
        obj.__wbg_ptr = ptr;
        MarkFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    static __unwrap(jsValue) {
        if (!(jsValue instanceof Mark)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        MarkFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_mark_free(ptr, 0);
    }
    /**
     * Add this mark to a set of marks, returning a new sorted set.
     * @param {Mark[]} set
     * @returns {Mark[]}
     */
    addToSet(set) {
        const ptr0 = passArrayJsValueToWasm0(set, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.mark_addToSet(this.__wbg_ptr, ptr0, len0);
        var v2 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v2;
    }
    /**
     * The mark's attributes as a JSON object.
     * @returns {any}
     */
    get attrs() {
        const ret = wasm.mark_attrs(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * True if this mark is equal to another mark.
     * @param {Mark} other
     * @returns {boolean}
     */
    eq(other) {
        _assertClass(other, Mark);
        const ret = wasm.mark_eq(this.__wbg_ptr, other.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * True if this mark is in the given set.
     * @param {Mark[]} set
     * @returns {boolean}
     */
    isInSet(set) {
        const ptr0 = passArrayJsValueToWasm0(set, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.mark_isInSet(this.__wbg_ptr, ptr0, len0);
        return ret !== 0;
    }
    /**
     * Remove this mark from a set of marks.
     * @param {Mark[]} set
     * @returns {Mark[]}
     */
    removeFromSet(set) {
        const ptr0 = passArrayJsValueToWasm0(set, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.mark_removeFromSet(this.__wbg_ptr, ptr0, len0);
        var v2 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v2;
    }
    /**
     * Test whether two mark sets are equal (same marks in the same order).
     * @param {Mark[]} a
     * @param {Mark[]} b
     * @returns {boolean}
     */
    static sameSet(a, b) {
        const ptr0 = passArrayJsValueToWasm0(a, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArrayJsValueToWasm0(b, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.mark_sameSet(ptr0, len0, ptr1, len1);
        return ret !== 0;
    }
    /**
     * Create a sorted, deduplicated mark set from an array of marks.
     * @param {Schema} schema
     * @param {Mark[]} marks
     * @returns {Mark[]}
     */
    static setFrom(schema, marks) {
        _assertClass(schema, Schema);
        const ptr0 = passArrayJsValueToWasm0(marks, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.mark_setFrom(schema.__wbg_ptr, ptr0, len0);
        var v2 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v2;
    }
    /**
     * JSON representation: `{type, attrs}`.
     * @returns {any}
     */
    toJSON() {
        const ret = wasm.mark_toJSON(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * The mark type.
     * @returns {MarkType}
     */
    get type_() {
        const ret = wasm.mark_type_(this.__wbg_ptr);
        return MarkType.__wrap(ret);
    }
}
if (Symbol.dispose) Mark.prototype[Symbol.dispose] = Mark.prototype.free;

/**
 * A mark type in the schema.
 */
export class MarkType {
    static __wrap(ptr) {
        const obj = Object.create(MarkType.prototype);
        obj.__wbg_ptr = ptr;
        MarkTypeFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        MarkTypeFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_marktype_free(ptr, 0);
    }
    /**
     * Create a mark of this type.
     * @param {any} attrs
     * @returns {Mark}
     */
    create(attrs) {
        const ret = wasm.marktype_create(this.__wbg_ptr, attrs);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return Mark.__wrap(ret[0]);
    }
    /**
     * True if this mark type excludes the given mark type.
     * @param {MarkType} other
     * @returns {boolean}
     */
    excludes(other) {
        _assertClass(other, MarkType);
        const ret = wasm.marktype_excludes(this.__wbg_ptr, other.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Find the first mark of this type in the given set.
     * @param {Mark[]} marks
     * @returns {Mark | undefined}
     */
    isInSet(marks) {
        const ptr0 = passArrayJsValueToWasm0(marks, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.marktype_isInSet(this.__wbg_ptr, ptr0, len0);
        return ret === 0 ? undefined : Mark.__wrap(ret);
    }
    /**
     * The name of this mark type.
     * @returns {string}
     */
    get name() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.marktype_name(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Remove all marks of this type from the given set.
     * @param {Mark[]} marks
     * @returns {Mark[]}
     */
    removeFromSet(marks) {
        const ptr0 = passArrayJsValueToWasm0(marks, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.marktype_removeFromSet(this.__wbg_ptr, ptr0, len0);
        var v2 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v2;
    }
    /**
     * The schema this mark type belongs to.
     * @returns {Schema}
     */
    get schema() {
        const ret = wasm.marktype_schema(this.__wbg_ptr);
        return Schema.__wrap(ret);
    }
    /**
     * The spec for this mark type as a JSON object.
     * @returns {any}
     */
    get spec() {
        const ret = wasm.marktype_spec(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
}
if (Symbol.dispose) MarkType.prototype[Symbol.dispose] = MarkType.prototype.free;

/**
 * A node in a ProseMirror document tree.
 */
export class Node {
    static __wrap(ptr) {
        const obj = Object.create(Node.prototype);
        obj.__wbg_ptr = ptr;
        NodeFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    static __unwrap(jsValue) {
        if (!(jsValue instanceof Node)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        NodeFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_node_free(ptr, 0);
    }
    /**
     * The node's attributes as a JSON object.
     * @returns {any}
     */
    get attrs() {
        const ret = wasm.node_attrs(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * True if the given node can be appended to this one.
     * @param {Node} other
     * @returns {boolean}
     */
    canAppend(other) {
        _assertClass(other, Node);
        const ret = wasm.node_canAppend(this.__wbg_ptr, other.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Check whether the given fragment can replace the content at `from..to`.
     * @param {number} from
     * @param {number} to
     * @param {Fragment | null | undefined} replacement
     * @param {number} start
     * @param {number | null} [end]
     * @returns {boolean}
     */
    canReplace(from, to, replacement, start, end) {
        let ptr0 = 0;
        if (!isLikeNone(replacement)) {
            _assertClass(replacement, Fragment);
            ptr0 = replacement.__destroy_into_raw();
        }
        const ret = wasm.node_canReplace(this.__wbg_ptr, from, to, ptr0, start, isLikeNone(end) ? Number.MAX_SAFE_INTEGER : (end) >>> 0);
        return ret !== 0;
    }
    /**
     * Check whether a node of the given type can replace the content at `from..to`.
     * @param {number} from
     * @param {number} to
     * @param {NodeType} type_
     * @returns {boolean}
     */
    canReplaceWith(from, to, type_) {
        _assertClass(type_, NodeType);
        const ret = wasm.node_canReplaceWith(this.__wbg_ptr, from, to, type_.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Check if this node conforms to the schema.
     */
    check() {
        const ret = wasm.node_check(this.__wbg_ptr);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Get the child at the given index.
     * @param {number} index
     * @returns {Node | undefined}
     */
    child(index) {
        const ret = wasm.node_child(this.__wbg_ptr, index);
        return ret === 0 ? undefined : Node.__wrap(ret);
    }
    /**
     * Returns `{node, index, offset}` for the child immediately after `pos`,
     * or null.
     * @param {number} pos
     * @returns {object | undefined}
     */
    childAfter(pos) {
        const ret = wasm.node_childAfter(this.__wbg_ptr, pos);
        return ret;
    }
    /**
     * Returns `{node, index, offset}` for the child immediately before `pos`,
     * or null.
     * @param {number} pos
     * @returns {object | undefined}
     */
    childBefore(pos) {
        const ret = wasm.node_childBefore(this.__wbg_ptr, pos);
        return ret;
    }
    /**
     * Number of child nodes.
     * @returns {number}
     */
    get childCount() {
        const ret = wasm.node_childCount(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The content fragment of this node.
     * @returns {Fragment}
     */
    get content() {
        const ret = wasm.node_content(this.__wbg_ptr);
        return Fragment.__wrap(ret);
    }
    /**
     * Get the ContentMatch at the given child index.
     * @param {number} index
     * @returns {ContentMatch}
     */
    contentMatchAt(index) {
        const ret = wasm.node_contentMatchAt(this.__wbg_ptr, index);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ContentMatch.__wrap(ret[0]);
    }
    /**
     * Create a copy of this node with new content.
     * @param {Fragment} content
     * @returns {Node}
     */
    copy(content) {
        _assertClass(content, Fragment);
        const ret = wasm.node_copy(this.__wbg_ptr, content.__wbg_ptr);
        return Node.__wrap(ret);
    }
    /**
     * Cut out a portion of this node, returning a new node.
     * @param {number} from
     * @param {number} to
     * @returns {Node}
     */
    cut(from, to) {
        const ret = wasm.node_cut(this.__wbg_ptr, from, to);
        return Node.__wrap(ret);
    }
    /**
     * Call a function for all descendant nodes recursively.
     *
     * The callback receives `(node, pos, parent, index)`. Return false to stop.
     * @param {Function} f
     */
    descendants(f) {
        const ret = wasm.node_descendants(this.__wbg_ptr, f);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * True if this node is equal to another.
     * @param {Node} other
     * @returns {boolean}
     */
    eq(other) {
        _assertClass(other, Node);
        const ret = wasm.node_eq(this.__wbg_ptr, other.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * The first child, or null.
     * @returns {Node | undefined}
     */
    get firstChild() {
        const ret = wasm.node_firstChild(this.__wbg_ptr);
        return ret === 0 ? undefined : Node.__wrap(ret);
    }
    /**
     * Call a function for each direct child node.
     *
     * The callback receives `(node, offset, index)`.
     * @param {Function} f
     */
    forEach(f) {
        const ret = wasm.node_forEach(this.__wbg_ptr, f);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Test whether the node matches given type, attrs, and marks.
     *
     * `marks` can be null (don't check marks), an empty array (check for no marks),
     * or an array of Mark objects.
     * @param {NodeType} type_
     * @param {any} attrs
     * @param {Mark[] | null} [marks]
     * @returns {boolean}
     */
    hasMarkup(type_, attrs, marks) {
        _assertClass(type_, NodeType);
        var ptr0 = isLikeNone(marks) ? 0 : passArrayJsValueToWasm0(marks, wasm.__wbindgen_malloc);
        var len0 = WASM_VECTOR_LEN;
        const ret = wasm.node_hasMarkup(this.__wbg_ptr, type_.__wbg_ptr, attrs, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * True if this node allows inline content.
     * @returns {boolean}
     */
    get inlineContent() {
        const ret = wasm.node_inlineContent(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * True if this is an atom node.
     * @returns {boolean}
     */
    get isAtom() {
        const ret = wasm.node_isAtom(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * True if this is a block node.
     * @returns {boolean}
     */
    get isBlock() {
        const ret = wasm.node_isBlock(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * True if this is an inline node.
     * @returns {boolean}
     */
    get isInline() {
        const ret = wasm.node_isInline(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * True if this is a leaf node.
     * @returns {boolean}
     */
    get isLeaf() {
        const ret = wasm.node_isLeaf(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * True if this is a text node.
     * @returns {boolean}
     */
    get isText() {
        const ret = wasm.node_isText(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * True if this is a textblock node.
     * @returns {boolean}
     */
    get isTextblock() {
        const ret = wasm.node_isTextblock(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * The last child, or null.
     * @returns {Node | undefined}
     */
    get lastChild() {
        const ret = wasm.node_lastChild(this.__wbg_ptr);
        return ret === 0 ? undefined : Node.__wrap(ret);
    }
    /**
     * Apply marks to this node.
     * @param {Mark[]} marks
     * @returns {Node}
     */
    mark(marks) {
        const ptr0 = passArrayJsValueToWasm0(marks, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.node_mark(this.__wbg_ptr, ptr0, len0);
        return Node.__wrap(ret);
    }
    /**
     * The marks on this node, as an array of Mark objects.
     * @returns {Mark[]}
     */
    get marks() {
        const ret = wasm.node_marks(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Like `child`, but returns null if index is out of range.
     * @param {number} index
     * @returns {Node | undefined}
     */
    maybeChild(index) {
        const ret = wasm.node_maybeChild(this.__wbg_ptr, index);
        return ret === 0 ? undefined : Node.__wrap(ret);
    }
    /**
     * Get the node at the given position, if any.
     * @param {number} pos
     * @returns {Node | undefined}
     */
    nodeAt(pos) {
        const ret = wasm.node_nodeAt(this.__wbg_ptr, pos);
        return ret === 0 ? undefined : Node.__wrap(ret);
    }
    /**
     * The size of this node (including start/end tokens).
     * @returns {number}
     */
    get nodeSize() {
        const ret = wasm.node_nodeSize(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Call a function for all nodes between two positions.
     *
     * The callback receives `(node, pos, parent, index)`. Return false to stop.
     * @param {number} from
     * @param {number} to
     * @param {Function} f
     */
    nodesBetween(from, to, f) {
        const ret = wasm.node_nodesBetween(this.__wbg_ptr, from, to, f);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * True if a mark of the given type exists in the range.
     * @param {number} from
     * @param {number} to
     * @param {MarkType} mark_type
     * @returns {boolean}
     */
    rangeHasMark(from, to, mark_type) {
        _assertClass(mark_type, MarkType);
        const ret = wasm.node_rangeHasMark(this.__wbg_ptr, from, to, mark_type.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Replace content in this node.
     * @param {number} from
     * @param {number} to
     * @param {Slice} slice
     * @returns {Node}
     */
    replace(from, to, slice) {
        _assertClass(slice, Slice);
        const ret = wasm.node_replace(this.__wbg_ptr, from, to, slice.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return Node.__wrap(ret[0]);
    }
    /**
     * Resolve a position in this node.
     * @param {number} pos
     * @returns {ResolvedPos}
     */
    resolve(pos) {
        const ret = wasm.node_resolve(this.__wbg_ptr, pos);
        return ResolvedPos.__wrap(ret);
    }
    /**
     * Resolve a position without using a cache (same as `resolve` in this implementation).
     * @param {number} pos
     * @returns {ResolvedPos}
     */
    resolveNoCache(pos) {
        const ret = wasm.node_resolveNoCache(this.__wbg_ptr, pos);
        return ResolvedPos.__wrap(ret);
    }
    /**
     * True if this node has the same markup as another (same type, attrs, marks).
     * @param {Node} other
     * @returns {boolean}
     */
    sameMarkup(other) {
        _assertClass(other, Node);
        const ret = wasm.node_sameMarkup(this.__wbg_ptr, other.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Cut out a slice from this node.
     * Note: wasm-bindgen JS wrapper strips the 3rd bool param from the JS
     * signature — use slice_with_parents for include_parents=true.
     * @param {number} from
     * @param {number} to
     * @returns {Slice}
     */
    slice(from, to) {
        const ret = wasm.node_slice(this.__wbg_ptr, from, to);
        return Slice.__wrap(ret);
    }
    /**
     * Cut out a slice including parent nodes.
     * @param {number} from
     * @param {number} to
     * @returns {Slice}
     */
    sliceWithParents(from, to) {
        const ret = wasm.node_sliceWithParents(this.__wbg_ptr, from, to);
        return Slice.__wrap(ret);
    }
    /**
     * The text content if this is a text node.
     * @returns {string | undefined}
     */
    get text() {
        const ret = wasm.node_text(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * Get the text content between two positions.
     * @param {number} from
     * @param {number} to
     * @param {any} block_sep
     * @param {any} leaf_text
     * @returns {string}
     */
    textBetween(from, to, block_sep, leaf_text) {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.node_textBetween(this.__wbg_ptr, from, to, block_sep, leaf_text);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The concatenated text of all text node descendants.
     * @returns {string}
     */
    get textContent() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.node_textContent(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * JSON representation of this node.
     * @returns {any}
     */
    toJSON() {
        const ret = wasm.node_toJSON(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Debug string representation.
     * @returns {string}
     */
    toString() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.node_toString(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The node type.
     * @returns {NodeType}
     */
    get type_() {
        const ret = wasm.node_type_(this.__wbg_ptr);
        return NodeType.__wrap(ret);
    }
}
if (Symbol.dispose) Node.prototype[Symbol.dispose] = Node.prototype.free;

/**
 * A range across a single node in the document.
 */
export class NodeRange {
    static __wrap(ptr) {
        const obj = Object.create(NodeRange.prototype);
        obj.__wbg_ptr = ptr;
        NodeRangeFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        NodeRangeFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_noderange_free(ptr, 0);
    }
    /**
     * The resolved position at the start of the range.
     * @returns {ResolvedPos}
     */
    get $from() {
        const ret = wasm.noderange__from(this.__wbg_ptr);
        return ResolvedPos.__wrap(ret);
    }
    /**
     * The resolved position at the end of the range.
     * @returns {ResolvedPos}
     */
    get $to() {
        const ret = wasm.noderange__to(this.__wbg_ptr);
        return ResolvedPos.__wrap(ret);
    }
    /**
     * The depth of the node that the range points into.
     * @returns {number}
     */
    get depth() {
        const ret = wasm.noderange_depth(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The end position of the range's node.
     * @returns {number}
     */
    get end() {
        const ret = wasm.noderange_end(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The end index of the range's node in its parent.
     * @returns {number}
     */
    get endIndex() {
        const ret = wasm.noderange_endIndex(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The start position of the range.
     * @returns {number}
     */
    get from() {
        const ret = wasm.noderange_from(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The parent node.
     * @returns {Node}
     */
    get parent() {
        const ret = wasm.noderange_parent(this.__wbg_ptr);
        return Node.__wrap(ret);
    }
    /**
     * The start position of the range's node.
     * @returns {number}
     */
    get start() {
        const ret = wasm.noderange_start(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The start index of the range's node in its parent.
     * @returns {number}
     */
    get startIndex() {
        const ret = wasm.noderange_startIndex(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The end position of the range.
     * @returns {number}
     */
    get to() {
        const ret = wasm.noderange_to(this.__wbg_ptr);
        return ret >>> 0;
    }
}
if (Symbol.dispose) NodeRange.prototype[Symbol.dispose] = NodeRange.prototype.free;

/**
 * A node type in the schema.
 */
export class NodeType {
    static __wrap(ptr) {
        const obj = Object.create(NodeType.prototype);
        obj.__wbg_ptr = ptr;
        NodeTypeFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        NodeTypeFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_nodetype_free(ptr, 0);
    }
    /**
     * Filter the given marks to only those allowed by this node type.
     * @param {Mark[]} marks
     * @returns {Mark[]}
     */
    allowedMarks(marks) {
        const ptr0 = passArrayJsValueToWasm0(marks, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.nodetype_allowedMarks(this.__wbg_ptr, ptr0, len0);
        var v2 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v2;
    }
    /**
     * True if this node type allows the given mark type.
     * @param {MarkType} mark_type
     * @returns {boolean}
     */
    allowsMarkType(mark_type) {
        _assertClass(mark_type, MarkType);
        const ret = wasm.nodetype_allowsMarkType(this.__wbg_ptr, mark_type.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * True if this node type allows the given set of marks.
     * @param {Mark[]} marks
     * @returns {boolean}
     */
    allowsMarks(marks) {
        const ptr0 = passArrayJsValueToWasm0(marks, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.nodetype_allowsMarks(this.__wbg_ptr, ptr0, len0);
        return ret !== 0;
    }
    /**
     * Default attributes for this node type, as a JSON object.
     * @returns {any}
     */
    get attrs() {
        const ret = wasm.nodetype_attrs(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * True if this node type's content can be placed in the other node type.
     * @param {NodeType} other
     * @returns {boolean}
     */
    compatibleContent(other) {
        _assertClass(other, NodeType);
        const ret = wasm.nodetype_compatibleContent(this.__wbg_ptr, other.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Get the ContentMatch for this node type's content expression.
     * @returns {ContentMatch | undefined}
     */
    get contentMatch() {
        const ret = wasm.nodetype_contentMatch(this.__wbg_ptr);
        return ret === 0 ? undefined : ContentMatch.__wrap(ret);
    }
    /**
     * Create a node of this type.
     * @param {any} attrs
     * @param {Fragment | null | undefined} content
     * @param {Mark[]} marks
     * @returns {Node}
     */
    create(attrs, content, marks) {
        let ptr0 = 0;
        if (!isLikeNone(content)) {
            _assertClass(content, Fragment);
            ptr0 = content.__destroy_into_raw();
        }
        const ptr1 = passArrayJsValueToWasm0(marks, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.nodetype_create(this.__wbg_ptr, attrs, ptr0, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return Node.__wrap(ret[0]);
    }
    /**
     * Create a node of this type and fill in the content if possible.
     * @param {any} attrs
     * @param {Fragment | null | undefined} content
     * @param {Mark[]} marks
     * @returns {Node | undefined}
     */
    createAndFill(attrs, content, marks) {
        let ptr0 = 0;
        if (!isLikeNone(content)) {
            _assertClass(content, Fragment);
            ptr0 = content.__destroy_into_raw();
        }
        const ptr1 = passArrayJsValueToWasm0(marks, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.nodetype_createAndFill(this.__wbg_ptr, attrs, ptr0, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === 0 ? undefined : Node.__wrap(ret[0]);
    }
    /**
     * Create a node of this type, returning an error if invalid.
     * @param {any} attrs
     * @param {Fragment | null | undefined} content
     * @param {Mark[]} marks
     * @returns {Node}
     */
    createChecked(attrs, content, marks) {
        let ptr0 = 0;
        if (!isLikeNone(content)) {
            _assertClass(content, Fragment);
            ptr0 = content.__destroy_into_raw();
        }
        const ptr1 = passArrayJsValueToWasm0(marks, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.nodetype_createChecked(this.__wbg_ptr, attrs, ptr0, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return Node.__wrap(ret[0]);
    }
    /**
     * True if this node type has required attributes.
     * @returns {boolean}
     */
    hasRequiredAttrs() {
        const ret = wasm.nodetype_hasRequiredAttrs(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * True if this node type allows inline content.
     * @returns {boolean}
     */
    get inlineContent() {
        const ret = wasm.nodetype_inlineContent(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * True if this is an atom node type.
     * @returns {boolean}
     */
    get isAtom() {
        const ret = wasm.nodetype_isAtom(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * True if this is a block node type.
     * @returns {boolean}
     */
    get isBlock() {
        const ret = wasm.nodetype_isBlock(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * True if this node type is rendered as pre-formatted (code).
     * @returns {boolean}
     */
    get isCode() {
        const ret = wasm.nodetype_isCode(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * True if this node type belongs to the given group.
     * @param {string} group
     * @returns {boolean}
     */
    isInGroup(group) {
        const ptr0 = passStringToWasm0(group, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.nodetype_isInGroup(this.__wbg_ptr, ptr0, len0);
        return ret !== 0;
    }
    /**
     * True if this is an inline node type.
     * @returns {boolean}
     */
    get isInline() {
        const ret = wasm.nodetype_isInline(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * True if this is a leaf node type.
     * @returns {boolean}
     */
    get isLeaf() {
        const ret = wasm.nodetype_isLeaf(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * True if this is the text node type.
     * @returns {boolean}
     */
    get isText() {
        const ret = wasm.nodetype_isText(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * True if this is a textblock node type.
     * @returns {boolean}
     */
    get isTextblock() {
        const ret = wasm.nodetype_isTextblock(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * The set of mark types allowed by this node type (null if all marks are allowed).
     * @returns {MarkType[] | undefined}
     */
    get markSet() {
        const ret = wasm.nodetype_markSet(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        }
        return v1;
    }
    /**
     * The name of this node type.
     * @returns {string}
     */
    get name() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.nodetype_name(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The schema this node type belongs to.
     * @returns {Schema}
     */
    get schema() {
        const ret = wasm.nodetype_schema(this.__wbg_ptr);
        return Schema.__wrap(ret);
    }
    /**
     * The spec for this node type as a JSON object.
     * @returns {any}
     */
    get spec() {
        const ret = wasm.nodetype_spec(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Check whether the given fragment is valid content for this node type.
     * @param {Fragment} fragment
     * @returns {boolean}
     */
    validContent(fragment) {
        _assertClass(fragment, Fragment);
        const ret = wasm.nodetype_validContent(this.__wbg_ptr, fragment.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * The whitespace behaviour for this node type.
     * @returns {string}
     */
    get whitespace() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.nodetype_whitespace(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
}
if (Symbol.dispose) NodeType.prototype[Symbol.dispose] = NodeType.prototype.free;

/**
 * A resolved position in a document.
 */
export class ResolvedPos {
    static __wrap(ptr) {
        const obj = Object.create(ResolvedPos.prototype);
        obj.__wbg_ptr = ptr;
        ResolvedPosFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ResolvedPosFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_resolvedpos_free(ptr, 0);
    }
    /**
     * The position after the ancestor at the given depth.
     * @param {number | null} [depth]
     * @returns {number | undefined}
     */
    after(depth) {
        const ret = wasm.resolvedpos_after(this.__wbg_ptr, isLikeNone(depth) ? Number.MAX_SAFE_INTEGER : (depth) >>> 0);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
    /**
     * The position before the ancestor at the given depth.
     * @param {number | null} [depth]
     * @returns {number | undefined}
     */
    before(depth) {
        const ret = wasm.resolvedpos_before(this.__wbg_ptr, isLikeNone(depth) ? Number.MAX_SAFE_INTEGER : (depth) >>> 0);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
    /**
     * The block range around this position, optionally extended to another.
     * @param {ResolvedPos} other
     * @returns {NodeRange | undefined}
     */
    blockRange(other) {
        _assertClass(other, ResolvedPos);
        const ret = wasm.resolvedpos_blockRange(this.__wbg_ptr, other.__wbg_ptr);
        return ret === 0 ? undefined : NodeRange.__wrap(ret);
    }
    /**
     * The depth of this position.
     * @returns {number}
     */
    get depth() {
        const ret = wasm.resolvedpos_depth(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The document node.
     * @returns {Node}
     */
    get doc() {
        const ret = wasm.resolvedpos_doc(this.__wbg_ptr);
        return Node.__wrap(ret);
    }
    /**
     * The end position of the ancestor at the given depth.
     * @param {number | null} [depth]
     * @returns {number}
     */
    end(depth) {
        const ret = wasm.resolvedpos_end(this.__wbg_ptr, isLikeNone(depth) ? Number.MAX_SAFE_INTEGER : (depth) >>> 0);
        return ret >>> 0;
    }
    /**
     * The index into the ancestor at the given depth.
     * @param {number | null} [depth]
     * @returns {number}
     */
    index(depth) {
        const ret = wasm.resolvedpos_index(this.__wbg_ptr, isLikeNone(depth) ? Number.MAX_SAFE_INTEGER : (depth) >>> 0);
        return ret >>> 0;
    }
    /**
     * The index after the ancestor at the given depth.
     * @param {number | null} [depth]
     * @returns {number}
     */
    indexAfter(depth) {
        const ret = wasm.resolvedpos_indexAfter(this.__wbg_ptr, isLikeNone(depth) ? Number.MAX_SAFE_INTEGER : (depth) >>> 0);
        return ret >>> 0;
    }
    /**
     * The marks at this position.
     * @returns {Mark[]}
     */
    marks() {
        const ret = wasm.resolvedpos_marks(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * The marks across this position and another.
     * @param {ResolvedPos} end
     * @returns {Mark[] | undefined}
     */
    marksAcross(end) {
        _assertClass(end, ResolvedPos);
        const ret = wasm.resolvedpos_marksAcross(this.__wbg_ptr, end.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        }
        return v1;
    }
    /**
     * Return the greater of this and another resolved position.
     * @param {ResolvedPos} other
     * @returns {ResolvedPos}
     */
    max(other) {
        _assertClass(other, ResolvedPos);
        const ret = wasm.resolvedpos_max(this.__wbg_ptr, other.__wbg_ptr);
        return ResolvedPos.__wrap(ret);
    }
    /**
     * Return the lesser of this and another resolved position.
     * @param {ResolvedPos} other
     * @returns {ResolvedPos}
     */
    min(other) {
        _assertClass(other, ResolvedPos);
        const ret = wasm.resolvedpos_min(this.__wbg_ptr, other.__wbg_ptr);
        return ResolvedPos.__wrap(ret);
    }
    /**
     * Get the ancestor node at the given depth.
     * @param {number | null} [depth]
     * @returns {Node}
     */
    node(depth) {
        const ret = wasm.resolvedpos_node(this.__wbg_ptr, isLikeNone(depth) ? Number.MAX_SAFE_INTEGER : (depth) >>> 0);
        return Node.__wrap(ret);
    }
    /**
     * The node immediately after this position, if any.
     * @returns {Node | undefined}
     */
    get nodeAfter() {
        const ret = wasm.resolvedpos_nodeAfter(this.__wbg_ptr);
        return ret === 0 ? undefined : Node.__wrap(ret);
    }
    /**
     * The node immediately before this position, if any.
     * @returns {Node | undefined}
     */
    get nodeBefore() {
        const ret = wasm.resolvedpos_nodeBefore(this.__wbg_ptr);
        return ret === 0 ? undefined : Node.__wrap(ret);
    }
    /**
     * The parent node containing this position.
     * @returns {Node}
     */
    get parent() {
        const ret = wasm.resolvedpos_parent(this.__wbg_ptr);
        return Node.__wrap(ret);
    }
    /**
     * The offset of this position into its parent text node.
     * @returns {number}
     */
    get parentOffset() {
        const ret = wasm.resolvedpos_parentOffset(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The absolute position.
     * @returns {number}
     */
    get pos() {
        const ret = wasm.resolvedpos_pos(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The position at the given index in the ancestor at the given depth.
     * @param {number} index
     * @param {number | null} [depth]
     * @returns {number}
     */
    posAtIndex(index, depth) {
        const ret = wasm.resolvedpos_posAtIndex(this.__wbg_ptr, index, isLikeNone(depth) ? Number.MAX_SAFE_INTEGER : (depth) >>> 0);
        return ret >>> 0;
    }
    /**
     * True if this position shares the same parent with another.
     * @param {ResolvedPos} other
     * @returns {boolean}
     */
    sameParent(other) {
        _assertClass(other, ResolvedPos);
        const ret = wasm.resolvedpos_sameParent(this.__wbg_ptr, other.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * The shared depth between this position and another.
     * @param {number} pos
     * @returns {number}
     */
    sharedDepth(pos) {
        const ret = wasm.resolvedpos_sharedDepth(this.__wbg_ptr, pos);
        return ret >>> 0;
    }
    /**
     * The start position of the ancestor at the given depth.
     * @param {number | null} [depth]
     * @returns {number}
     */
    start(depth) {
        const ret = wasm.resolvedpos_start(this.__wbg_ptr, isLikeNone(depth) ? Number.MAX_SAFE_INTEGER : (depth) >>> 0);
        return ret >>> 0;
    }
    /**
     * The offset into the parent text node.
     * @returns {number}
     */
    get textOffset() {
        const ret = wasm.resolvedpos_textOffset(this.__wbg_ptr);
        return ret >>> 0;
    }
}
if (Symbol.dispose) ResolvedPos.prototype[Symbol.dispose] = ResolvedPos.prototype.free;

/**
 * A ProseMirror document schema, built from a JSON spec.
 */
export class Schema {
    static __wrap(ptr) {
        const obj = Object.create(Schema.prototype);
        obj.__wbg_ptr = ptr;
        SchemaFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        SchemaFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_schema_free(ptr, 0);
    }
    /**
     * Create a mark of the given type.
     * @param {string} type_name
     * @param {any} attrs
     * @returns {Mark}
     */
    mark(type_name, attrs) {
        const ptr0 = passStringToWasm0(type_name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.schema_mark(this.__wbg_ptr, ptr0, len0, attrs);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return Mark.__wrap(ret[0]);
    }
    /**
     * Create a mark from a JSON representation.
     * @param {any} json
     * @returns {Mark}
     */
    markFromJSON(json) {
        const ret = wasm.schema_markFromJSON(this.__wbg_ptr, json);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return Mark.__wrap(ret[0]);
    }
    /**
     * All mark types in this schema, keyed by name.
     * @returns {object}
     */
    get marks() {
        const ret = wasm.schema_marks(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Create a new Schema from a JSON `SchemaSpec` string.
     * @param {string} spec_json
     */
    constructor(spec_json) {
        const ptr0 = passStringToWasm0(spec_json, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.schema_new(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        SchemaFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Create a node of the given type.
     *
     * `attrs` can be a JSON object or null.
     * `content` is an optional Fragment.
     * @param {string} type_name
     * @param {any} attrs
     * @param {Fragment | null | undefined} content
     * @param {Mark[]} marks
     * @returns {Node}
     */
    node(type_name, attrs, content, marks) {
        const ptr0 = passStringToWasm0(type_name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        let ptr1 = 0;
        if (!isLikeNone(content)) {
            _assertClass(content, Fragment);
            ptr1 = content.__destroy_into_raw();
        }
        const ptr2 = passArrayJsValueToWasm0(marks, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.schema_node(this.__wbg_ptr, ptr0, len0, attrs, ptr1, ptr2, len2);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return Node.__wrap(ret[0]);
    }
    /**
     * Create a node from a JSON representation.
     * @param {any} json
     * @returns {Node}
     */
    nodeFromJSON(json) {
        const ret = wasm.schema_nodeFromJSON(this.__wbg_ptr, json);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return Node.__wrap(ret[0]);
    }
    /**
     * All node types in this schema, keyed by name.
     * @returns {object}
     */
    get nodes() {
        const ret = wasm.schema_nodes(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Create a text node, optionally with marks.
     * @param {string} text
     * @param {Mark[]} marks
     * @returns {Node}
     */
    text(text, marks) {
        const ptr0 = passStringToWasm0(text, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArrayJsValueToWasm0(marks, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.schema_text(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return Node.__wrap(ret);
    }
    /**
     * The top-level node type (typically "doc").
     * @returns {NodeType | undefined}
     */
    get topNodeType() {
        const ret = wasm.schema_topNodeType(this.__wbg_ptr);
        return ret === 0 ? undefined : NodeType.__wrap(ret);
    }
}
if (Symbol.dispose) Schema.prototype[Symbol.dispose] = Schema.prototype.free;

/**
 * A slice represents a piece cut out of a document.
 */
export class Slice {
    static __wrap(ptr) {
        const obj = Object.create(Slice.prototype);
        obj.__wbg_ptr = ptr;
        SliceFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        SliceFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_slice_free(ptr, 0);
    }
    /**
     * The content fragment of this slice.
     * @returns {Fragment}
     */
    get content() {
        const ret = wasm.slice_content(this.__wbg_ptr);
        return Fragment.__wrap(ret);
    }
    /**
     * Create an empty slice for the given schema.
     * @param {Schema} schema
     * @returns {Slice}
     */
    static empty(schema) {
        _assertClass(schema, Schema);
        const ret = wasm.slice_empty(schema.__wbg_ptr);
        return Slice.__wrap(ret);
    }
    /**
     * True if this slice is equal to another.
     * @param {Slice} other
     * @returns {boolean}
     */
    eq(other) {
        _assertClass(other, Slice);
        const ret = wasm.slice_eq(this.__wbg_ptr, other.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Create a new slice from a fragment.
     * @param {Fragment} content
     * @param {number} open_start
     * @param {number} open_end
     */
    constructor(content, open_start, open_end) {
        _assertClass(content, Fragment);
        const ret = wasm.slice_new(content.__wbg_ptr, open_start, open_end);
        this.__wbg_ptr = ret;
        SliceFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * The open depth at the end.
     * @returns {number}
     */
    get openEnd() {
        const ret = wasm.slice_openEnd(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The open depth at the start.
     * @returns {number}
     */
    get openStart() {
        const ret = wasm.slice_openStart(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The size of this slice.
     * @returns {number}
     */
    get size() {
        const ret = wasm.slice_size(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * JSON representation of this slice.
     * @returns {any}
     */
    toJSON() {
        const ret = wasm.slice_toJSON(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
}
if (Symbol.dispose) Slice.prototype[Symbol.dispose] = Slice.prototype.free;

export class StepMap_ {
    static __wrap(ptr) {
        const obj = Object.create(StepMap_.prototype);
        obj.__wbg_ptr = ptr;
        StepMap_Finalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    static __unwrap(jsValue) {
        if (!(jsValue instanceof StepMap_)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        StepMap_Finalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_stepmap__free(ptr, 0);
    }
    /**
     * Call the given callback for each entry in the map.
     * The callback receives (oldStart, oldEnd, newStart, newEnd).
     * @param {Function} f
     */
    forEach(f) {
        const ret = wasm.stepmap__forEach(this.__wbg_ptr, f);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Return the inverse of this step map.
     * @returns {StepMap_}
     */
    invert() {
        const ret = wasm.stepmap__invert(this.__wbg_ptr);
        return StepMap_.__wrap(ret);
    }
    /**
     * Map a position through this step map.
     * @param {number} pos
     * @param {number | null} [bias]
     * @returns {number}
     */
    map(pos, bias) {
        const ret = wasm.stepmap__map(this.__wbg_ptr, pos, isLikeNone(bias) ? Number.MAX_SAFE_INTEGER : (bias) >> 0);
        return ret >>> 0;
    }
    /**
     * Map a position, returning a full MapResult.
     * @param {number} pos
     * @param {number | null} [bias]
     * @returns {MapResult_}
     */
    mapResult(pos, bias) {
        const ret = wasm.stepmap__mapResult(this.__wbg_ptr, pos, isLikeNone(bias) ? Number.MAX_SAFE_INTEGER : (bias) >> 0);
        return MapResult_.__wrap(ret);
    }
    /**
     * Create a new StepMap from flat range data.
     * Each group of three numbers represents (oldStart, oldEnd, newStart).
     * @param {Uint32Array} ranges
     */
    constructor(ranges) {
        const ptr0 = passArray32ToWasm0(ranges, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.stepmap__new(ptr0, len0);
        this.__wbg_ptr = ret;
        StepMap_Finalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * The raw ranges backing this map.
     * @returns {Uint32Array}
     */
    get ranges() {
        const ret = wasm.stepmap__ranges(this.__wbg_ptr);
        var v1 = getArrayU32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Recover a position from a packed recovery value.
     * @param {number} value
     * @returns {number | undefined}
     */
    recover(value) {
        const ret = wasm.stepmap__recover(this.__wbg_ptr, value);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
}
if (Symbol.dispose) StepMap_.prototype[Symbol.dispose] = StepMap_.prototype.free;

export class Step_ {
    static __wrap(ptr) {
        const obj = Object.create(Step_.prototype);
        obj.__wbg_ptr = ptr;
        Step_Finalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        Step_Finalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_step__free(ptr, 0);
    }
    /**
     * Create an AddMarkStep.
     * @param {number} from
     * @param {number} to
     * @param {Mark} mark
     * @returns {Step_}
     */
    static addMark(from, to, mark) {
        _assertClass(mark, Mark);
        const ret = wasm.step__addMark(from, to, mark.__wbg_ptr);
        return Step_.__wrap(ret);
    }
    /**
     * Create an AddNodeMarkStep.
     * @param {number} pos
     * @param {Mark} mark
     * @returns {Step_}
     */
    static addNodeMark(pos, mark) {
        _assertClass(mark, Mark);
        const ret = wasm.step__addNodeMark(pos, mark.__wbg_ptr);
        return Step_.__wrap(ret);
    }
    /**
     * Apply this step to a document.
     * Returns a JS object `{ doc, failed }`. On success, `failed` is null.
     * @param {Node} doc
     * @returns {any}
     */
    apply(doc) {
        _assertClass(doc, Node);
        const ret = wasm.step__apply(this.__wbg_ptr, doc.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Create an AttrStep.
     * @param {number} pos
     * @param {string} attr
     * @param {any} value
     * @returns {Step_}
     */
    static attr(pos, attr, value) {
        const ptr0 = passStringToWasm0(attr, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.step__attr(pos, ptr0, len0, value);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return Step_.__wrap(ret[0]);
    }
    /**
     * Create a DocAttrStep.
     * @param {string} attr
     * @param {any} value
     * @returns {Step_}
     */
    static docAttr(attr, value) {
        const ptr0 = passStringToWasm0(attr, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.step__docAttr(ptr0, len0, value);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return Step_.__wrap(ret[0]);
    }
    /**
     * Deserialize a step from its JSON representation.
     * @param {Schema} schema
     * @param {string} json_str
     * @returns {Step_}
     */
    static fromJSON(schema, json_str) {
        _assertClass(schema, Schema);
        const ptr0 = passStringToWasm0(json_str, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.step__fromJSON(schema.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return Step_.__wrap(ret[0]);
    }
    /**
     * Get the StepMap that describes the position changes caused by this step.
     * @returns {StepMap_}
     */
    getMap() {
        const ret = wasm.step__getMap(this.__wbg_ptr);
        return StepMap_.__wrap(ret);
    }
    /**
     * Create an inverted version of this step.
     * @param {Node} doc
     * @returns {Step_}
     */
    invert(doc) {
        _assertClass(doc, Node);
        const ret = wasm.step__invert(this.__wbg_ptr, doc.__wbg_ptr);
        return Step_.__wrap(ret);
    }
    /**
     * Map this step through a mapping. Returns null if the step was
     * invalidated.
     * @param {Mapping_} mapping
     * @returns {Step_ | undefined}
     */
    map(mapping) {
        _assertClass(mapping, Mapping_);
        const ret = wasm.step__map(this.__wbg_ptr, mapping.__wbg_ptr);
        return ret === 0 ? undefined : Step_.__wrap(ret);
    }
    /**
     * Try to merge this step with another step.
     * @param {Step_} other
     * @returns {Step_ | undefined}
     */
    merge(other) {
        _assertClass(other, Step_);
        const ret = wasm.step__merge(this.__wbg_ptr, other.__wbg_ptr);
        return ret === 0 ? undefined : Step_.__wrap(ret);
    }
    /**
     * Create a RemoveMarkStep.
     * @param {number} from
     * @param {number} to
     * @param {Mark} mark
     * @returns {Step_}
     */
    static removeMark(from, to, mark) {
        _assertClass(mark, Mark);
        const ret = wasm.step__removeMark(from, to, mark.__wbg_ptr);
        return Step_.__wrap(ret);
    }
    /**
     * Create a RemoveNodeMarkStep.
     * @param {number} pos
     * @param {Mark} mark
     * @returns {Step_}
     */
    static removeNodeMark(pos, mark) {
        _assertClass(mark, Mark);
        const ret = wasm.step__removeNodeMark(pos, mark.__wbg_ptr);
        return Step_.__wrap(ret);
    }
    /**
     * Create a ReplaceStep.
     * @param {number} from
     * @param {number} to
     * @param {Slice | null} [slice]
     * @param {boolean | null} [structure]
     * @returns {Step_}
     */
    static replace(from, to, slice, structure) {
        let ptr0 = 0;
        if (!isLikeNone(slice)) {
            _assertClass(slice, Slice);
            ptr0 = slice.__destroy_into_raw();
        }
        const ret = wasm.step__replace(from, to, ptr0, isLikeNone(structure) ? 0xFFFFFF : structure ? 1 : 0);
        return Step_.__wrap(ret);
    }
    /**
     * Create a ReplaceAroundStep.
     * @param {number} from
     * @param {number} to
     * @param {number} gap_from
     * @param {number} gap_to
     * @param {Slice | null | undefined} slice
     * @param {number} insert
     * @param {boolean | null} [structure]
     * @returns {Step_}
     */
    static replaceAround(from, to, gap_from, gap_to, slice, insert, structure) {
        let ptr0 = 0;
        if (!isLikeNone(slice)) {
            _assertClass(slice, Slice);
            ptr0 = slice.__destroy_into_raw();
        }
        const ret = wasm.step__replaceAround(from, to, gap_from, gap_to, ptr0, insert, isLikeNone(structure) ? 0xFFFFFF : structure ? 1 : 0);
        return Step_.__wrap(ret);
    }
    /**
     * Serialize this step to its JSON representation.
     * @returns {string}
     */
    toJSON() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.step__toJSON(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
}
if (Symbol.dispose) Step_.prototype[Symbol.dispose] = Step_.prototype.free;

export class Transform_ {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        Transform_Finalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_transform__free(ptr, 0);
    }
    /**
     * Add a mark to inline content in the given range.
     * @param {number} from
     * @param {number} to
     * @param {Mark} mark
     */
    addMark(from, to, mark) {
        _assertClass(mark, Mark);
        wasm.transform__addMark(this.__wbg_ptr, from, to, mark.__wbg_ptr);
    }
    /**
     * Add a mark to a specific node.
     * @param {number} pos
     * @param {Mark} mark
     */
    addNodeMark(pos, mark) {
        _assertClass(mark, Mark);
        wasm.transform__addNodeMark(this.__wbg_ptr, pos, mark.__wbg_ptr);
    }
    /**
     * The document before any steps were applied.
     * @returns {Node}
     */
    get before() {
        const ret = wasm.transform__before(this.__wbg_ptr);
        return Node.__wrap(ret);
    }
    /**
     * Return the total changed range as `{from, to}`, or null if nothing
     * changed.
     * @returns {any | undefined}
     */
    changedRange() {
        const ret = wasm.transform__changedRange(this.__wbg_ptr);
        return ret;
    }
    /**
     * Remove content at the given position that is incompatible with the
     * given node type.
     * @param {number} pos
     * @param {NodeType} node_type
     * @param {boolean} clear_newlines
     */
    clearIncompatible(pos, node_type, clear_newlines) {
        _assertClass(node_type, NodeType);
        wasm.transform__clearIncompatible(this.__wbg_ptr, pos, node_type.__wbg_ptr, clear_newlines);
    }
    /**
     * Delete content between two positions.
     * @param {number} from
     * @param {number} to
     */
    delete(from, to) {
        wasm.transform__delete(this.__wbg_ptr, from, to);
    }
    /**
     * Delete a range, adjusting to avoid leaving invalid structure.
     * @param {number} from
     * @param {number} to
     */
    deleteRange(from, to) {
        wasm.transform__deleteRange(this.__wbg_ptr, from, to);
    }
    /**
     * The current document.
     * @returns {Node}
     */
    get doc() {
        const ret = wasm.transform__doc(this.__wbg_ptr);
        return Node.__wrap(ret);
    }
    /**
     * Whether any steps have been applied to change the document.
     * @returns {boolean}
     */
    get docChanged() {
        const ret = wasm.transform__docChanged(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * The intermediate documents after each step.
     * @returns {Node[]}
     */
    get docs() {
        const ret = wasm.transform__docs(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Insert content at a position.
     * @param {number} pos
     * @param {Node} content
     */
    insert(pos, content) {
        _assertClass(content, Node);
        wasm.transform__insert(this.__wbg_ptr, pos, content.__wbg_ptr);
    }
    /**
     * Join nodes at the given position.
     * @param {number} pos
     * @param {number | null} [depth]
     */
    join(pos, depth) {
        wasm.transform__join(this.__wbg_ptr, pos, isLikeNone(depth) ? Number.MAX_SAFE_INTEGER : (depth) >>> 0);
    }
    /**
     * Lift content out of its parent node to the given target depth.
     * @param {NodeRange} range
     * @param {number} target
     */
    lift(range, target) {
        _assertClass(range, NodeRange);
        const ret = wasm.transform__lift(this.__wbg_ptr, range.__wbg_ptr, target);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * The accumulated mapping from applied steps.
     * @returns {Mapping_}
     */
    get mapping() {
        const ret = wasm.transform__mapping(this.__wbg_ptr);
        return Mapping_.__wrap(ret);
    }
    /**
     * Apply a step, returning an error message on failure or null on success.
     * @param {Step_} step
     * @returns {string | undefined}
     */
    maybeStep(step) {
        _assertClass(step, Step_);
        const ret = wasm.transform__maybeStep(this.__wbg_ptr, step.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * Create a new transform starting from the given document.
     * @param {Node} doc
     */
    constructor(doc) {
        _assertClass(doc, Node);
        const ret = wasm.transform__new(doc.__wbg_ptr);
        this.__wbg_ptr = ret;
        Transform_Finalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Remove mark(s) from inline content in the given range.
     * @param {number} from
     * @param {number} to
     * @param {Mark | null} [mark]
     */
    removeMark(from, to, mark) {
        let ptr0 = 0;
        if (!isLikeNone(mark)) {
            _assertClass(mark, Mark);
            ptr0 = mark.__destroy_into_raw();
        }
        wasm.transform__removeMark(this.__wbg_ptr, from, to, ptr0);
    }
    /**
     * Remove all marks of the given type from inline content in the given range.
     * @param {number} from
     * @param {number} to
     * @param {MarkType} mark_type
     */
    removeMarkType(from, to, mark_type) {
        _assertClass(mark_type, MarkType);
        wasm.transform__removeMarkType(this.__wbg_ptr, from, to, mark_type.__wbg_ptr);
    }
    /**
     * Remove a mark from a specific node.
     * @param {number} pos
     * @param {Mark} mark
     */
    removeNodeMark(pos, mark) {
        _assertClass(mark, Mark);
        wasm.transform__removeNodeMark(this.__wbg_ptr, pos, mark.__wbg_ptr);
    }
    /**
     * Remove all marks of the given type from a specific node.
     * @param {number} pos
     * @param {MarkType} mark_type
     */
    removeNodeMarkType(pos, mark_type) {
        _assertClass(mark_type, MarkType);
        wasm.transform__removeNodeMarkType(this.__wbg_ptr, pos, mark_type.__wbg_ptr);
    }
    /**
     * Replace the content between `from` and `to` (if given) with the given
     * slice (if given).
     * @param {number} from
     * @param {number | null} [to]
     * @param {Slice | null} [slice]
     */
    replace(from, to, slice) {
        let ptr0 = 0;
        if (!isLikeNone(slice)) {
            _assertClass(slice, Slice);
            ptr0 = slice.__destroy_into_raw();
        }
        wasm.transform__replace(this.__wbg_ptr, from, isLikeNone(to) ? Number.MAX_SAFE_INTEGER : (to) >>> 0, ptr0);
    }
    /**
     * Replace a range with a slice, fitting the start and end.
     * @param {number} from
     * @param {number} to
     * @param {Slice} slice
     */
    replaceRange(from, to, slice) {
        _assertClass(slice, Slice);
        wasm.transform__replaceRange(this.__wbg_ptr, from, to, slice.__wbg_ptr);
    }
    /**
     * Replace a range with a single node, fitting the start and end.
     * @param {number} from
     * @param {number} to
     * @param {Node} node
     */
    replaceRangeWith(from, to, node) {
        _assertClass(node, Node);
        wasm.transform__replaceRangeWith(this.__wbg_ptr, from, to, node.__wbg_ptr);
    }
    /**
     * Replace a range with the content of the given node.
     * @param {number} from
     * @param {number} to
     * @param {Node} content
     */
    replaceWith(from, to, content) {
        _assertClass(content, Node);
        wasm.transform__replaceWith(this.__wbg_ptr, from, to, content.__wbg_ptr);
    }
    /**
     * Change the type and attributes of all text blocks in the given range.
     * @param {number} from
     * @param {number | null | undefined} to
     * @param {NodeType} type_
     * @param {any} attrs
     */
    setBlockType(from, to, type_, attrs) {
        _assertClass(type_, NodeType);
        const ret = wasm.transform__setBlockType(this.__wbg_ptr, from, isLikeNone(to) ? Number.MAX_SAFE_INTEGER : (to) >>> 0, type_.__wbg_ptr, attrs);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Set an attribute on the document root.
     * @param {string} attr
     * @param {any} value
     */
    setDocAttribute(attr, value) {
        const ptr0 = passStringToWasm0(attr, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.transform__setDocAttribute(this.__wbg_ptr, ptr0, len0, value);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Set an attribute on the node at the given position.
     * @param {number} pos
     * @param {string} attr
     * @param {any} value
     */
    setNodeAttribute(pos, attr, value) {
        const ptr0 = passStringToWasm0(attr, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.transform__setNodeAttribute(this.__wbg_ptr, pos, ptr0, len0, value);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Change the type, attributes, and/or marks of a node at the given
     * position.
     * @param {number} pos
     * @param {NodeType | null | undefined} type_
     * @param {any} attrs
     * @param {Mark[] | null} [marks]
     */
    setNodeMarkup(pos, type_, attrs, marks) {
        let ptr0 = 0;
        if (!isLikeNone(type_)) {
            _assertClass(type_, NodeType);
            ptr0 = type_.__destroy_into_raw();
        }
        var ptr1 = isLikeNone(marks) ? 0 : passArrayJsValueToWasm0(marks, wasm.__wbindgen_malloc);
        var len1 = WASM_VECTOR_LEN;
        const ret = wasm.transform__setNodeMarkup(this.__wbg_ptr, pos, ptr0, attrs, ptr1, len1);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Split the node at the given position.
     * @param {number} pos
     * @param {number | null | undefined} depth
     * @param {any} types_after
     */
    split(pos, depth, types_after) {
        const ret = wasm.transform__split(this.__wbg_ptr, pos, isLikeNone(depth) ? Number.MAX_SAFE_INTEGER : (depth) >>> 0, types_after);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Apply a step.  Throws if the step is invalid.
     * @param {Step_} step
     */
    step(step) {
        _assertClass(step, Step_);
        const ret = wasm.transform__step(this.__wbg_ptr, step.__wbg_ptr);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * The steps applied so far.
     * @returns {Step_[]}
     */
    get steps() {
        const ret = wasm.transform__steps(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Wrap the content of a range in the given wrapper node specs.
     * @param {NodeRange} range
     * @param {any} wrappers
     */
    wrap(range, wrappers) {
        _assertClass(range, NodeRange);
        const ret = wasm.transform__wrap(this.__wbg_ptr, range.__wbg_ptr, wrappers);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
}
if (Symbol.dispose) Transform_.prototype[Symbol.dispose] = Transform_.prototype.free;

/**
 * Check whether the document can be joined at the given position.
 * @param {Node} doc
 * @param {number} pos
 * @returns {boolean}
 */
export function canJoin(doc, pos) {
    _assertClass(doc, Node);
    const ret = wasm.canJoin(doc.__wbg_ptr, pos);
    return ret !== 0;
}

/**
 * Check whether the document can be split at the given position.
 * @param {Node} doc
 * @param {number} pos
 * @param {number | null | undefined} depth
 * @param {any} types_after
 * @returns {boolean}
 */
export function canSplit(doc, pos, depth, types_after) {
    _assertClass(doc, Node);
    const ret = wasm.canSplit(doc.__wbg_ptr, pos, isLikeNone(depth) ? Number.MAX_SAFE_INTEGER : (depth) >>> 0, types_after);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0] !== 0;
}

/**
 * Parse a content expression string into a ContentMatch.
 *
 * `node_types` should be an object where each key is a node type name
 * and each value is either a plain object with a `group` property (e.g.
 * `{group: "block"}`) or a NodeType instance with a `spec()` method.
 * @param {string} expr
 * @param {object} node_types
 * @returns {ContentMatch}
 */
export function contentMatchParse(expr, node_types) {
    const ptr0 = passStringToWasm0(expr, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.contentMatchParse(ptr0, len0, node_types);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ContentMatch.__wrap(ret[0]);
}

/**
 * Find a valid drop point for the given slice near `pos`.
 * @param {Node} doc
 * @param {number} pos
 * @param {Slice} slice
 * @returns {number | undefined}
 */
export function dropPoint(doc, pos, slice) {
    _assertClass(doc, Node);
    _assertClass(slice, Slice);
    const ret = wasm.dropPoint(doc.__wbg_ptr, pos, slice.__wbg_ptr);
    return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
}

/**
 * Find the wrapping node types needed to make `node_type` valid at the
 * given range.
 * @param {NodeRange} range
 * @param {NodeType} node_type
 * @param {any | null} [_attrs]
 * @returns {any | undefined}
 */
export function findWrapping(range, node_type, _attrs) {
    _assertClass(range, NodeRange);
    _assertClass(node_type, NodeType);
    const ret = wasm.findWrapping(range.__wbg_ptr, node_type.__wbg_ptr, isLikeNone(_attrs) ? 0 : addToExternrefTable0(_attrs));
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * Find a valid insertion point for the given node type near `pos`.
 * @param {Node} doc
 * @param {number} pos
 * @param {NodeType} node_type
 * @returns {number | undefined}
 */
export function insertPoint(doc, pos, node_type) {
    _assertClass(doc, Node);
    _assertClass(node_type, NodeType);
    const ret = wasm.insertPoint(doc.__wbg_ptr, pos, node_type.__wbg_ptr);
    return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
}

/**
 * Find a join point near the given position.
 * @param {Node} doc
 * @param {number} pos
 * @param {number | null} [dir]
 * @returns {number | undefined}
 */
export function joinPoint(doc, pos, dir) {
    _assertClass(doc, Node);
    const ret = wasm.joinPoint(doc.__wbg_ptr, pos, isLikeNone(dir) ? Number.MAX_SAFE_INTEGER : (dir) >> 0);
    return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
}

/**
 * Find the depth to which the given range can be lifted, if any.
 * @param {NodeRange} range
 * @returns {number | undefined}
 */
export function liftTarget(range) {
    _assertClass(range, NodeRange);
    const ret = wasm.liftTarget(range.__wbg_ptr);
    return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
}
function __wbg_get_imports() {
    const import0 = {
        __proto__: null,
        __wbg_Error_ef53bc310eb298a0: function(arg0, arg1) {
            const ret = Error(getStringFromWasm0(arg0, arg1));
            return ret;
        },
        __wbg_String_8564e559799eccda: function(arg0, arg1) {
            const ret = String(arg1);
            const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg___wbindgen_bigint_get_as_i64_38130e98eecd467d: function(arg0, arg1) {
            const v = arg1;
            const ret = typeof(v) === 'bigint' ? v : undefined;
            getDataViewMemory0().setBigInt64(arg0 + 8 * 1, isLikeNone(ret) ? BigInt(0) : ret, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, !isLikeNone(ret), true);
        },
        __wbg___wbindgen_boolean_get_1a45e2c38d4d41b9: function(arg0) {
            const v = arg0;
            const ret = typeof(v) === 'boolean' ? v : undefined;
            return isLikeNone(ret) ? 0xFFFFFF : ret ? 1 : 0;
        },
        __wbg___wbindgen_debug_string_0accd80f45e5faa2: function(arg0, arg1) {
            const ret = debugString(arg1);
            const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg___wbindgen_in_70a403a56e771704: function(arg0, arg1) {
            const ret = arg0 in arg1;
            return ret;
        },
        __wbg___wbindgen_is_bigint_6ffd6468a9bc44b9: function(arg0) {
            const ret = typeof(arg0) === 'bigint';
            return ret;
        },
        __wbg___wbindgen_is_function_754e9f305ff6029e: function(arg0) {
            const ret = typeof(arg0) === 'function';
            return ret;
        },
        __wbg___wbindgen_is_null_87c3bfe968c6a5ad: function(arg0) {
            const ret = arg0 === null;
            return ret;
        },
        __wbg___wbindgen_is_object_56732c2bc353f41d: function(arg0) {
            const val = arg0;
            const ret = typeof(val) === 'object' && val !== null;
            return ret;
        },
        __wbg___wbindgen_is_undefined_67b456be8673d3d7: function(arg0) {
            const ret = arg0 === undefined;
            return ret;
        },
        __wbg___wbindgen_jsval_eq_1068e624fa87f6ab: function(arg0, arg1) {
            const ret = arg0 === arg1;
            return ret;
        },
        __wbg___wbindgen_jsval_loose_eq_2c56564c75129511: function(arg0, arg1) {
            const ret = arg0 == arg1;
            return ret;
        },
        __wbg___wbindgen_number_get_9bb1761122181af2: function(arg0, arg1) {
            const obj = arg1;
            const ret = typeof(obj) === 'number' ? obj : undefined;
            getDataViewMemory0().setFloat64(arg0 + 8 * 1, isLikeNone(ret) ? 0 : ret, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, !isLikeNone(ret), true);
        },
        __wbg___wbindgen_string_get_72bdf95d3ae505b1: function(arg0, arg1) {
            const obj = arg1;
            const ret = typeof(obj) === 'string' ? obj : undefined;
            var ptr1 = isLikeNone(ret) ? 0 : passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            var len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg___wbindgen_throw_1506f2235d1bdba0: function(arg0, arg1) {
            throw new Error(getStringFromWasm0(arg0, arg1));
        },
        __wbg_call_6e37a87ff352da3d: function() { return handleError(function (arg0, arg1, arg2, arg3, arg4) {
            const ret = arg0.call(arg1, arg2, arg3, arg4);
            return ret;
        }, arguments); },
        __wbg_call_7d39dd526ab44e10: function() { return handleError(function (arg0, arg1, arg2, arg3, arg4, arg5) {
            const ret = arg0.call(arg1, arg2, arg3, arg4, arg5);
            return ret;
        }, arguments); },
        __wbg_call_8a89609d89f6608a: function() { return handleError(function (arg0, arg1) {
            const ret = arg0.call(arg1);
            return ret;
        }, arguments); },
        __wbg_done_60cf307fcc680536: function(arg0) {
            const ret = arg0.done;
            return ret;
        },
        __wbg_entries_04b37a02507f1713: function(arg0) {
            const ret = Object.entries(arg0);
            return ret;
        },
        __wbg_get_1f8f054ddbaa7db2: function() { return handleError(function (arg0, arg1) {
            const ret = Reflect.get(arg0, arg1);
            return ret;
        }, arguments); },
        __wbg_get_2b48c7d0d006a781: function(arg0, arg1) {
            const ret = arg0[arg1 >>> 0];
            return ret;
        },
        __wbg_get_de6a0f7d4d18a304: function() { return handleError(function (arg0, arg1) {
            const ret = Reflect.get(arg0, arg1);
            return ret;
        }, arguments); },
        __wbg_get_unchecked_33f6e5c9e2f2d6b2: function(arg0, arg1) {
            const ret = arg0[arg1 >>> 0];
            return ret;
        },
        __wbg_instanceof_ArrayBuffer_8f49811467741499: function(arg0) {
            let result;
            try {
                result = arg0 instanceof ArrayBuffer;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_instanceof_Map_9fc06d9a951bcee6: function(arg0) {
            let result;
            try {
                result = arg0 instanceof Map;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_instanceof_Uint8Array_86f30649f63ef9c2: function(arg0) {
            let result;
            try {
                result = arg0 instanceof Uint8Array;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_isArray_67c2c9c4313f4448: function(arg0) {
            const ret = Array.isArray(arg0);
            return ret;
        },
        __wbg_isSafeInteger_66acec27e09e99a7: function(arg0) {
            const ret = Number.isSafeInteger(arg0);
            return ret;
        },
        __wbg_iterator_8732428d309e270e: function() {
            const ret = Symbol.iterator;
            return ret;
        },
        __wbg_keys_19cb80412a6f5349: function(arg0) {
            const ret = Object.keys(arg0);
            return ret;
        },
        __wbg_length_4a591ecaa01354d9: function(arg0) {
            const ret = arg0.length;
            return ret;
        },
        __wbg_length_66f1a4b2e9026940: function(arg0) {
            const ret = arg0.length;
            return ret;
        },
        __wbg_mark_new: function(arg0) {
            const ret = Mark.__wrap(arg0);
            return ret;
        },
        __wbg_mark_unwrap: function(arg0) {
            const ret = Mark.__unwrap(arg0);
            return ret;
        },
        __wbg_marktype_new: function(arg0) {
            const ret = MarkType.__wrap(arg0);
            return ret;
        },
        __wbg_new_578aeef4b6b94378: function(arg0) {
            const ret = new Uint8Array(arg0);
            return ret;
        },
        __wbg_new_ce1ab61c1c2b300d: function() {
            const ret = new Object();
            return ret;
        },
        __wbg_new_d90091b82fdf5b91: function() {
            const ret = new Array();
            return ret;
        },
        __wbg_next_9e03acdf51c4960d: function(arg0) {
            const ret = arg0.next;
            return ret;
        },
        __wbg_next_eb8ca7351fa27906: function() { return handleError(function (arg0) {
            const ret = arg0.next();
            return ret;
        }, arguments); },
        __wbg_node_new: function(arg0) {
            const ret = Node.__wrap(arg0);
            return ret;
        },
        __wbg_node_unwrap: function(arg0) {
            const ret = Node.__unwrap(arg0);
            return ret;
        },
        __wbg_nodetype_new: function(arg0) {
            const ret = NodeType.__wrap(arg0);
            return ret;
        },
        __wbg_prototypesetcall_3249fc62a0fafa30: function(arg0, arg1, arg2) {
            Uint8Array.prototype.set.call(getArrayU8FromWasm0(arg0, arg1), arg2);
        },
        __wbg_push_a6822215aa43e71c: function(arg0, arg1) {
            const ret = arg0.push(arg1);
            return ret;
        },
        __wbg_set_6e30c9374c26414c: function() { return handleError(function (arg0, arg1, arg2) {
            const ret = Reflect.set(arg0, arg1, arg2);
            return ret;
        }, arguments); },
        __wbg_step__new: function(arg0) {
            const ret = Step_.__wrap(arg0);
            return ret;
        },
        __wbg_stepmap__new: function(arg0) {
            const ret = StepMap_.__wrap(arg0);
            return ret;
        },
        __wbg_stepmap__unwrap: function(arg0) {
            const ret = StepMap_.__unwrap(arg0);
            return ret;
        },
        __wbg_value_f3625092ee4b37f4: function(arg0) {
            const ret = arg0.value;
            return ret;
        },
        __wbindgen_cast_0000000000000001: function(arg0) {
            // Cast intrinsic for `F64 -> Externref`.
            const ret = arg0;
            return ret;
        },
        __wbindgen_cast_0000000000000002: function(arg0) {
            // Cast intrinsic for `I64 -> Externref`.
            const ret = arg0;
            return ret;
        },
        __wbindgen_cast_0000000000000003: function(arg0, arg1) {
            // Cast intrinsic for `Ref(String) -> Externref`.
            const ret = getStringFromWasm0(arg0, arg1);
            return ret;
        },
        __wbindgen_cast_0000000000000004: function(arg0) {
            // Cast intrinsic for `U64 -> Externref`.
            const ret = BigInt.asUintN(64, arg0);
            return ret;
        },
        __wbindgen_init_externref_table: function() {
            const table = wasm.__wbindgen_externrefs;
            const offset = table.grow(4);
            table.set(0, undefined);
            table.set(offset + 0, undefined);
            table.set(offset + 1, null);
            table.set(offset + 2, true);
            table.set(offset + 3, false);
        },
    };
    return {
        __proto__: null,
        "./prosemirror_rs_wasm_bg.js": import0,
    };
}

const ContentMatchFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_contentmatch_free(ptr, 1));
const FragmentFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_fragment_free(ptr, 1));
const MapResult_Finalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_mapresult__free(ptr, 1));
const Mapping_Finalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_mapping__free(ptr, 1));
const MarkFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_mark_free(ptr, 1));
const MarkTypeFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_marktype_free(ptr, 1));
const NodeFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_node_free(ptr, 1));
const NodeRangeFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_noderange_free(ptr, 1));
const NodeTypeFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_nodetype_free(ptr, 1));
const ResolvedPosFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_resolvedpos_free(ptr, 1));
const SchemaFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_schema_free(ptr, 1));
const SliceFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_slice_free(ptr, 1));
const StepMap_Finalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_stepmap__free(ptr, 1));
const Step_Finalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_step__free(ptr, 1));
const Transform_Finalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_transform__free(ptr, 1));

function addToExternrefTable0(obj) {
    const idx = wasm.__externref_table_alloc();
    wasm.__wbindgen_externrefs.set(idx, obj);
    return idx;
}

function _assertClass(instance, klass) {
    if (!(instance instanceof klass)) {
        throw new Error(`expected instance of ${klass.name}`);
    }
}

function debugString(val) {
    // primitive types
    const type = typeof val;
    if (type == 'number' || type == 'boolean' || val == null) {
        return  `${val}`;
    }
    if (type == 'string') {
        return `"${val}"`;
    }
    if (type == 'symbol') {
        const description = val.description;
        if (description == null) {
            return 'Symbol';
        } else {
            return `Symbol(${description})`;
        }
    }
    if (type == 'function') {
        const name = val.name;
        if (typeof name == 'string' && name.length > 0) {
            return `Function(${name})`;
        } else {
            return 'Function';
        }
    }
    // objects
    if (Array.isArray(val)) {
        const length = val.length;
        let debug = '[';
        if (length > 0) {
            debug += debugString(val[0]);
        }
        for(let i = 1; i < length; i++) {
            debug += ', ' + debugString(val[i]);
        }
        debug += ']';
        return debug;
    }
    // Test for built-in
    const builtInMatches = /\[object ([^\]]+)\]/.exec(toString.call(val));
    let className;
    if (builtInMatches && builtInMatches.length > 1) {
        className = builtInMatches[1];
    } else {
        // Failed to match the standard '[object ClassName]'
        return toString.call(val);
    }
    if (className == 'Object') {
        // we're a user defined class or Object
        // JSON.stringify avoids problems with cycles, and is generally much
        // easier than looping through ownProperties of `val`.
        try {
            return 'Object(' + JSON.stringify(val) + ')';
        } catch (_) {
            return 'Object';
        }
    }
    // errors
    if (val instanceof Error) {
        return `${val.name}: ${val.message}\n${val.stack}`;
    }
    // TODO we could test for more things here, like `Set`s and `Map`s.
    return className;
}

function getArrayJsValueFromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    const mem = getDataViewMemory0();
    const result = [];
    for (let i = ptr; i < ptr + 4 * len; i += 4) {
        result.push(wasm.__wbindgen_externrefs.get(mem.getUint32(i, true)));
    }
    wasm.__externref_drop_slice(ptr, len);
    return result;
}

function getArrayU32FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint32ArrayMemory0().subarray(ptr / 4, ptr / 4 + len);
}

function getArrayU8FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint8ArrayMemory0().subarray(ptr / 1, ptr / 1 + len);
}

let cachedDataViewMemory0 = null;
function getDataViewMemory0() {
    if (cachedDataViewMemory0 === null || cachedDataViewMemory0.buffer.detached === true || (cachedDataViewMemory0.buffer.detached === undefined && cachedDataViewMemory0.buffer !== wasm.memory.buffer)) {
        cachedDataViewMemory0 = new DataView(wasm.memory.buffer);
    }
    return cachedDataViewMemory0;
}

function getStringFromWasm0(ptr, len) {
    return decodeText(ptr >>> 0, len);
}

let cachedUint32ArrayMemory0 = null;
function getUint32ArrayMemory0() {
    if (cachedUint32ArrayMemory0 === null || cachedUint32ArrayMemory0.byteLength === 0) {
        cachedUint32ArrayMemory0 = new Uint32Array(wasm.memory.buffer);
    }
    return cachedUint32ArrayMemory0;
}

let cachedUint8ArrayMemory0 = null;
function getUint8ArrayMemory0() {
    if (cachedUint8ArrayMemory0 === null || cachedUint8ArrayMemory0.byteLength === 0) {
        cachedUint8ArrayMemory0 = new Uint8Array(wasm.memory.buffer);
    }
    return cachedUint8ArrayMemory0;
}

function handleError(f, args) {
    try {
        return f.apply(this, args);
    } catch (e) {
        const idx = addToExternrefTable0(e);
        wasm.__wbindgen_exn_store(idx);
    }
}

function isLikeNone(x) {
    return x === undefined || x === null;
}

function passArray32ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 4, 4) >>> 0;
    getUint32ArrayMemory0().set(arg, ptr / 4);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passArrayJsValueToWasm0(array, malloc) {
    const ptr = malloc(array.length * 4, 4) >>> 0;
    for (let i = 0; i < array.length; i++) {
        const add = addToExternrefTable0(array[i]);
        getDataViewMemory0().setUint32(ptr + 4 * i, add, true);
    }
    WASM_VECTOR_LEN = array.length;
    return ptr;
}

function passStringToWasm0(arg, malloc, realloc) {
    if (realloc === undefined) {
        const buf = cachedTextEncoder.encode(arg);
        const ptr = malloc(buf.length, 1) >>> 0;
        getUint8ArrayMemory0().subarray(ptr, ptr + buf.length).set(buf);
        WASM_VECTOR_LEN = buf.length;
        return ptr;
    }

    let len = arg.length;
    let ptr = malloc(len, 1) >>> 0;

    const mem = getUint8ArrayMemory0();

    let offset = 0;

    for (; offset < len; offset++) {
        const code = arg.charCodeAt(offset);
        if (code > 0x7F) break;
        mem[ptr + offset] = code;
    }
    if (offset !== len) {
        if (offset !== 0) {
            arg = arg.slice(offset);
        }
        ptr = realloc(ptr, len, len = offset + arg.length * 3, 1) >>> 0;
        const view = getUint8ArrayMemory0().subarray(ptr + offset, ptr + len);
        const ret = cachedTextEncoder.encodeInto(arg, view);

        offset += ret.written;
        ptr = realloc(ptr, len, offset, 1) >>> 0;
    }

    WASM_VECTOR_LEN = offset;
    return ptr;
}

function takeFromExternrefTable0(idx) {
    const value = wasm.__wbindgen_externrefs.get(idx);
    wasm.__externref_table_dealloc(idx);
    return value;
}

let cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
cachedTextDecoder.decode();
const MAX_SAFARI_DECODE_BYTES = 2146435072;
let numBytesDecoded = 0;
function decodeText(ptr, len) {
    numBytesDecoded += len;
    if (numBytesDecoded >= MAX_SAFARI_DECODE_BYTES) {
        cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
        cachedTextDecoder.decode();
        numBytesDecoded = len;
    }
    return cachedTextDecoder.decode(getUint8ArrayMemory0().subarray(ptr, ptr + len));
}

const cachedTextEncoder = new TextEncoder();

if (!('encodeInto' in cachedTextEncoder)) {
    cachedTextEncoder.encodeInto = function (arg, view) {
        const buf = cachedTextEncoder.encode(arg);
        view.set(buf);
        return {
            read: arg.length,
            written: buf.length
        };
    };
}

let WASM_VECTOR_LEN = 0;

let wasmModule, wasmInstance, wasm;
function __wbg_finalize_init(instance, module) {
    wasmInstance = instance;
    wasm = instance.exports;
    wasmModule = module;
    cachedDataViewMemory0 = null;
    cachedUint32ArrayMemory0 = null;
    cachedUint8ArrayMemory0 = null;
    wasm.__wbindgen_start();
    return wasm;
}

async function __wbg_load(module, imports) {
    if (typeof Response === 'function' && module instanceof Response) {
        if (typeof WebAssembly.instantiateStreaming === 'function') {
            try {
                return await WebAssembly.instantiateStreaming(module, imports);
            } catch (e) {
                const validResponse = module.ok && expectedResponseType(module.type);

                if (validResponse && module.headers.get('Content-Type') !== 'application/wasm') {
                    console.warn("`WebAssembly.instantiateStreaming` failed because your server does not serve Wasm with `application/wasm` MIME type. Falling back to `WebAssembly.instantiate` which is slower. Original error:\n", e);

                } else { throw e; }
            }
        }

        const bytes = await module.arrayBuffer();
        return await WebAssembly.instantiate(bytes, imports);
    } else {
        const instance = await WebAssembly.instantiate(module, imports);

        if (instance instanceof WebAssembly.Instance) {
            return { instance, module };
        } else {
            return instance;
        }
    }

    function expectedResponseType(type) {
        switch (type) {
            case 'basic': case 'cors': case 'default': return true;
        }
        return false;
    }
}

function initSync(module) {
    if (wasm !== undefined) return wasm;


    if (module !== undefined) {
        if (Object.getPrototypeOf(module) === Object.prototype) {
            ({module} = module)
        } else {
            console.warn('using deprecated parameters for `initSync()`; pass a single object instead')
        }
    }

    const imports = __wbg_get_imports();
    if (!(module instanceof WebAssembly.Module)) {
        module = new WebAssembly.Module(module);
    }
    const instance = new WebAssembly.Instance(module, imports);
    return __wbg_finalize_init(instance, module);
}

async function __wbg_init(module_or_path) {
    if (wasm !== undefined) return wasm;


    if (module_or_path !== undefined) {
        if (Object.getPrototypeOf(module_or_path) === Object.prototype) {
            ({module_or_path} = module_or_path)
        } else {
            console.warn('using deprecated parameters for the initialization function; pass a single object instead')
        }
    }

    if (module_or_path === undefined) {
        module_or_path = new URL('prosemirror_rs_wasm_bg.wasm', import.meta.url);
    }
    const imports = __wbg_get_imports();

    if (typeof module_or_path === 'string' || (typeof Request === 'function' && module_or_path instanceof Request) || (typeof URL === 'function' && module_or_path instanceof URL)) {
        module_or_path = fetch(module_or_path);
    }

    const { instance, module } = await __wbg_load(await module_or_path, imports);

    return __wbg_finalize_init(instance, module);
}

export { initSync, __wbg_init as default };
